import type { Invoice, InvoiceTransitionAction } from '@/types';

/**
 * Zellen-Editierbarkeit im Grid — Spiegel der EDITABLE-Whitelist im
 * InvoiceController. Nicht-admin (Bauleiter/approver) sieht die Tabelle
 * rein lesend; die Review-Aktionen (freigeben/Korrektur/ablehnen) laufen
 * im Detail-Drawer.
 */
export type GridField =
  | 'invoice_number'
  | 'invoice_date'
  | 'due_date'
  | 'description'
  | 'contact_id'
  | 'bauvorhaben_id'
  | 'gewerk_id'
  | 'amount_net'
  | 'amount_tax'
  | 'amount_gross'
  | 'tax_rate_percent'
  | 'file'
  | 'payment';

// Felder, die auch nach der formalen Prüfung noch anpassbar sind
// (Zuordnungen + Betreff; Spiegel der EDITABLE-Whitelist im Controller).
const ASSIGNMENT_FIELDS: GridField[] = ['contact_id', 'bauvorhaben_id', 'gewerk_id', 'description'];

export function canEditCell(
  invoice: Pick<Invoice, 'status'>,
  field: GridField,
  isAdmin: boolean
): boolean {
  if (!isAdmin) return false;
  switch (invoice.status) {
    case 'erfasst':
      return true; // alle Felder inkl. Bezahlt
    case 'formal_geprueft':
      return ASSIGNMENT_FIELDS.includes(field);
    case 'korrektur_erforderlich':
      return ASSIGNMENT_FIELDS.includes(field) || field === 'file';
    case 'freigegeben':
    case 'an_lexware_uebergeben':
    case 'abgelehnt':
      return false;
    default:
      return false;
  }
}

/** Bezahlt-Toggle ist für admin in jedem Status erlaubt (Transition-Mark). */
export function canTogglePaid(isAdmin: boolean): boolean {
  return isAdmin;
}

/** Löschbarkeit = Whitelist im destroy() (erfasst/abgelehnt). */
export function canDelete(invoice: Pick<Invoice, 'status'>, isAdmin: boolean): boolean {
  return isAdmin && (invoice.status === 'erfasst' || invoice.status === 'abgelehnt');
}

/**
 * MwSt-Autoableitung: bei geändertem Satz Netto/Steuer aus dem Brutto
 * herleiten (Logik aus dem früheren InvoiceForm). Runden auf Cent.
 */
export function deriveNetTax(gross: number, ratePercent: number): { net: number; tax: number } | null {
  if (!Number.isFinite(gross) || !Number.isFinite(ratePercent) || gross <= 0) return null;
  const net = Math.round((gross / (1 + ratePercent / 100)) * 100) / 100;
  const tax = Math.round((gross - net) * 100) / 100;
  return { net, tax };
}

/** Client-Precheck für Netto+MwSt ≈ Brutto (gleiche Toleranz wie der Server). */
export function amountsMatch(net: number, tax: number, gross: number): boolean {
  return Math.abs(net + tax - gross) <= 0.02;
}

/** Statuswechsel, die direkt aus dem Grid möglich sind (Bezahlt-Achse). */
export function paidToggleAction(paid: boolean): InvoiceTransitionAction {
  return paid ? 'mark_paid' : 'mark_unpaid';
}